import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { PhotoCamera } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';

import {
  PhotoModerationService,
  PhotoModerationStats,
} from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import PageStyles from 'src/styles/PageStyles';
import { Table, TableInstance } from '../common/Table';
import PhotoModerationModal from './PhotoModerationModal';

function PhotoModeration() {
  const tableRef = React.useRef<TableInstance>();
  const { classes } = PageStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [sourceFilter, setSourceFilter] = useState('');
  const [stats, setStats] = useState<PhotoModerationStats | null>(null);

  const loadStats = async () => {
    try {
      const response = await PhotoModerationService.getStats();

      setStats(response.data.data);
    } catch (err) {
      console.warn('Failed to fetch moderation stats:', err);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
    tableRef.current?.fetchData();
    loadStats();
  };

  const COLUMNS: any = useMemo(
    () => [
      {
        id: 'photo-moderation',
        header: 'Photo Moderation',
        columns: [
          {
            header: 'User ID',
            accessorFn: row => String(row?.userId ?? ''),
            Cell: ({ cell }) => (
              <a
                href={`/users/lookup?id=${cell.row?.original?.userId}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {cell.row?.original?.userId}
              </a>
            ),
          },
          {
            header: 'Name',
            accessorFn: row => row?.userName ?? '',
          },
          {
            header: 'Photo',
            accessorFn: row => row?.newPhotoUrl ?? '',
            Cell: ({ cell }) => {
              const url = cell.row?.original?.newPhotoUrl;

              if (!url) {
                return (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: colors.gray[100],
                      border: '1px dashed',
                      borderColor: colors.gray[300],
                    }}
                  />
                );
              }

              return (
                <img
                  alt=''
                  src={url}
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                />
              );
            },
          },
          {
            header: 'Source',
            accessorFn: row => row?.source ?? '',
            Cell: ({ cell }) => (
              <Chip
                label={cell.row?.original?.source}
                color={cell.row?.original?.source === 'UPLOAD' ? 'primary' : 'default'}
                size='small'
              />
            ),
          },
          {
            header: 'Priority',
            accessorFn: row => String(row?.priority ?? ''),
          },
          {
            header: 'Created',
            accessorFn: row =>
              row?.created ? new Date(row.created).toLocaleDateString() : '',
          },
          {
            header: 'Status',
            accessorFn: row => row?.status ?? '',
            Cell: ({ cell }) => {
              const status = cell.row?.original?.status;
              const colorMap: Record<string, 'success' | 'error' | 'warning'> = {
                APPROVED: 'success',
                REJECTED: 'error',
                PENDING: 'warning',
              };

              return (
                <Chip
                  label={status}
                  color={colorMap[status] || 'default'}
                  size='small'
                />
              );
            },
          },
        ],
      },
    ],
    [],
  );

  const params = () => {
    const p: { key: string; value: string }[] = [];

    if (statusFilter) {
      p.push({ key: 'status', value: statusFilter });
    }
    if (sourceFilter) {
      p.push({ key: 'source', value: sourceFilter });
    }

    return p;
  };

  return (
    <Paper className={classes.paper}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h5'>Photo Moderation</Typography>
        <Box display='flex' alignItems='center' gap={2}>
          {stats && (
            <Typography variant='body2' color='textSecondary'>
              {stats.pendingCount} pending | {stats.reviewedTodayCount} reviewed today
            </Typography>
          )}
          <Button
            variant='contained'
            color='primary'
            startIcon={<PhotoCamera />}
            onClick={() => setModalOpen(true)}
          >
            Begin Moderation
          </Button>
        </Box>
      </Box>

      <Box display='flex' gap={2} mb={2}>
        <FormControl size='small' sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label='Status'
            onChange={e => {
              setStatusFilter(e.target.value);
              setTimeout(() => tableRef.current?.fetchData(), 0);
            }}
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='PENDING'>Pending</MenuItem>
            <MenuItem value='APPROVED'>Approved</MenuItem>
            <MenuItem value='REJECTED'>Rejected</MenuItem>
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 140 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            label='Source'
            onChange={e => {
              setSourceFilter(e.target.value);
              setTimeout(() => tableRef.current?.fetchData(), 0);
            }}
          >
            <MenuItem value=''>All</MenuItem>
            <MenuItem value='UPLOAD'>Upload</MenuItem>
            <MenuItem value='RENEWAL'>Renewal</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={PhotoModerationService.getByFilter}
        params={params}
        alwaysApplyParams
      />

      <PhotoModerationModal open={modalOpen} onClose={handleModalClose} />
    </Paper>
  );
}

export default withRouter(PhotoModeration);
